import {
  DefaultDemoScreenshotIllustration,
  DefaultRecordingScreenshotIllustration,
  LiveDemoPlaceholderIllustration,
  LiveTourPlaceholderIllustration,
  PlaybookPlaceholderIllustration,
  UrlPlaceholderIllustration,
} from '@carboncopyco/illustrations';
import classNames from 'classnames';
import type React from 'react';

import { Image } from '@carboncopyco/ui';

type RoundedSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

const roundedSizeToClassname: Partial<Record<RoundedSize, string>> = {
  sm: 'rounded',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  full: 'rounded-full',
};
const roundedTopSizeToClassname: Partial<Record<RoundedSize, string>> = {
  sm: 'rounded-t',
  md: 'rounded-t-md',
  lg: 'rounded-t-lg',
  xl: 'rounded-t-xl',
  '2xl': 'rounded-t-2xl',
};

export interface ScreenshotProps {
  overlay?: React.ReactNode;
  onClick?: React.MouseEventHandler;
  image?: string;
  alt?: string;
  rounded?: RoundedSize;
  recording?: boolean;
  withPadding?: boolean;
  withWhiteBorder?: boolean;
  noBorder?: boolean;
  customElement?: React.ReactNode;
  roundedTop?: boolean;
  isOverlayDemo?: boolean;
  isOverlayTour?: boolean;
  isUrl?: boolean;
  isPlaybook?: boolean;
}

export const Screenshot = ({
  overlay,
  onClick,
  rounded = 'md',
  image,
  alt = 'demo screenshot',
  recording,
  withPadding,
  noBorder,
  withWhiteBorder,
  customElement,
  roundedTop,
  isOverlayDemo,
  isOverlayTour,
  isUrl,
  isPlaybook,
}: ScreenshotProps) => {
  const radiusClass = roundedTop
    ? roundedTopSizeToClassname[rounded]
    : roundedSizeToClassname[rounded];

  const imageOrFallback = () => {
    if (customElement) {
      return customElement;
    }
    if (image) {
      return (
        <Image
          src={image}
          alt={alt}
          background
        />
      );
    }

    return recording ? (
      <DefaultRecordingScreenshotIllustration />
    ) : isOverlayDemo ? (
      <LiveDemoPlaceholderIllustration
        width="100%"
        height="100%"
      />
    ) : isOverlayTour ? (
      <LiveTourPlaceholderIllustration
        width="100%"
        height="100%"
      />
    ) : isPlaybook ? (
      <PlaybookPlaceholderIllustration
        width="100%"
        height="100%"
      />
    ) : isUrl ? (
      <UrlPlaceholderIllustration
        width="100%"
        height="100%"
      />
    ) : (
      <DefaultDemoScreenshotIllustration />
    );
  };

  return (
    <div
      onClick={onClick}
      className={classNames(
        'group w-full h-full overflow-hidden relative',
        radiusClass,
        {
          'hover:cursor-pointer': !!onClick,
          'border border-ds-natural-100': !noBorder,
          'border border-ds-accent3-100': withWhiteBorder,
        }
      )}
    >
      {!!overlay && (
        <div
          className={classNames(
            'absolute inset-0 invisible group-hover:visible',
            radiusClass
          )}
        >
          {overlay}
        </div>
      )}
      {withPadding ? (
        <div className="h-full py-4.5 px-7 bg-ds-natural-100 hover:cursor-pointer">
          {imageOrFallback()}
        </div>
      ) : (
        imageOrFallback()
      )}
    </div>
  );
};
